/**
 * API functions for the Social API core (SPEC PX-3 — social-planner R2;
 * mount renamed /api/social-posts -> /api/social/posts, deliberate, no
 * alias, by SPEC metabus-organic-changelog-2026-09-03 R3).
 *
 * Mirrors the house pattern (sequences.ts / prospects.ts): a local getAuthHeader()
 * pulling the Firebase token, raw fetch, { detail } error bodies.
 */

import { auth } from '../firebase';
import type {
  SocialPost,
  SocialPostCreate,
  SocialPostListResponse,
  SocialPostUpdate,
} from '@/types/socialPost';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function parseError(response: Response, fallback: string): Promise<never> {
  const error = await response.json().catch(() => ({ detail: response.statusText }));
  throw new Error(error.detail || `${fallback}: ${response.statusText}`);
}

export async function listSocialPosts(): Promise<SocialPostListResponse> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/social/posts`, { headers });
  if (!response.ok) {
    await parseError(response, 'Failed to list social posts');
  }
  return response.json();
}

export async function createSocialPost(data: SocialPostCreate): Promise<SocialPost> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/social/posts`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    await parseError(response, 'Failed to create social post');
  }
  return response.json();
}

export async function updateSocialPost(id: string, data: SocialPostUpdate): Promise<SocialPost> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/social/posts/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    await parseError(response, 'Failed to update social post');
  }
  return response.json();
}

export async function deleteSocialPost(id: string): Promise<{ status: string; post_id: string }> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/social/posts/${id}`, {
    method: 'DELETE',
    headers,
  });
  if (!response.ok) {
    await parseError(response, 'Failed to delete social post');
  }
  return response.json();
}

export async function publishSocialPost(id: string): Promise<Record<string, unknown>> {
  const headers = await getAuthHeader();
  const response = await fetch(`${API_BASE_URL}/api/social/posts/${id}/publish`, {
    method: 'POST',
    headers,
  });
  if (!response.ok) {
    await parseError(response, 'Failed to publish social post');
  }
  return response.json();
}
